import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  getJoinPreview, joinOrganization, joinByCode,
  getEmployeeInvitePreview, acceptEmployeeInvite,
  getClientInvitePreview, acceptClientInvite
} from '../services/businessService';
import './BusinessJoin.css';

export default function BusinessJoin({ mode }) {
  const params = useParams();
  const navigate = useNavigate();

  const token = params.token || params.joinToken;
  const isInviteMode = mode === 'employee-invite' || mode === 'client-invite';

  const [org, setOrg] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState('');
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    const loadPreview = async () => {
      try {
        if (mode === 'employee-invite') {
          const data = await getEmployeeInvitePreview(token);
          setInviteData(data);
          setOrg({ name: data.organizationName, businessType: data.businessType });
        } else if (mode === 'client-invite') {
          const data = await getClientInvitePreview(token);
          setInviteData(data);
          setOrg({ name: data.organizationName, businessType: data.businessType });
        } else {
          const data = await getJoinPreview(token);
          setOrg(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [token, mode]);

  const handleAcceptInvite = async () => {
    setJoining(true);
    try {
      let result;
      if (mode === 'employee-invite') {
        result = await acceptEmployeeInvite(token);
      } else {
        result = await acceptClientInvite(token);
      }
      setJoined(true);
      setResultMessage(result.message || `Joined ${org?.name}!`);
      toast.success(result.message || `Joined ${org?.name}!`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoining(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      const result = await joinOrganization(token);
      if (result.pendingVerification || org?.requireVerification) {
        setPendingVerification(true);
      }
      setJoined(true);
      setResultMessage(result.message || `Joined ${org?.name}!`);
      toast.success(result.message || `Joined ${org?.name}!`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoining(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (code.trim().length === 0) return;
    setCodeSubmitting(true);
    try {
      const result = await joinByCode(code.trim());
      if (result.pendingVerification) {
        setPendingVerification(true);
      }
      setJoined(true);
      setResultMessage(result.message || 'Joined successfully!');
      toast.success(result.message || 'Joined successfully!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCodeSubmitting(false);
    }
  };

  const roleLabel = mode === 'employee-invite' ? 'Employee' : 'Client';

  if (loading) {
    return (
      <div className="business-join">
        <div className="business-join-card">
          <div className="business-join-loading">Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-join">
        <div className="business-join-card">
          <div className="business-join-error">
            <h2>Unable to load invitation</h2>
            <p>{error}</p>
            <button className="business-join-btn business-join-btn-primary" onClick={() => navigate('/')}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="business-join">
        <div className="business-join-card">
          <div className="business-join-header">
            <h1 className="business-join-org-name">{org?.name}</h1>
          </div>
          <div className="business-join-success">
            {pendingVerification ? (
              <>
                <div className="business-join-success-icon">⏳</div>
                <h3>Request Submitted</h3>
                <p>
                  Your request to join <strong>{org?.name}</strong> has been submitted.
                  The business admin will review and approve your membership.
                </p>
              </>
            ) : (
              <>
                <div className="business-join-success-icon">✓</div>
                <h3>You're In!</h3>
                <p>{resultMessage || `You've successfully joined ${org?.name}.`}</p>
              </>
            )}
            <button
              className="business-join-btn business-join-btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={() => navigate('/')}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="business-join">
      <div className="business-join-card">
        <div className="business-join-header">
          {org?.businessType && (
            <div className="business-join-type">{org.businessType}</div>
          )}
          <h1 className="business-join-org-name">{org?.name}</h1>
          {isInviteMode && inviteData && (
            <p className="business-join-description">
              You've been invited to join as {mode === 'employee-invite' ? 'an' : 'a'} <strong>{roleLabel}</strong>
              {inviteData.employeeName && <> ({inviteData.employeeName})</>}
              {inviteData.clientName && <> ({inviteData.clientName})</>}
            </p>
          )}
          {!isInviteMode && org?.description && (
            <p className="business-join-description">{org.description}</p>
          )}
        </div>

        <div className="business-join-body">
          {isLoggedIn ? (
            <>
              {isInviteMode ? (
                <div className="business-join-invite-info">
                  <div className="business-join-invite-details">
                    {mode === 'employee-invite' && (
                      <p>As an employee, you'll be able to manage clients, assign exercise and nutrition plans, and track progress.</p>
                    )}
                    {mode === 'client-invite' && (
                      <p>As a client, you'll get personalized exercise and nutrition plans from your provider.</p>
                    )}
                    {inviteData?.title && (
                      <p className="business-join-title-info">Role: <strong>{inviteData.title}</strong></p>
                    )}
                  </div>
                  <button
                    className="business-join-btn business-join-btn-primary"
                    onClick={handleAcceptInvite}
                    disabled={joining}
                  >
                    {joining ? 'Accepting...' : `Accept ${roleLabel} Invitation`}
                  </button>
                </div>
              ) : (
                <button
                  className="business-join-btn business-join-btn-primary"
                  onClick={handleJoin}
                  disabled={joining}
                >
                  {joining ? 'Joining...' : `Join ${org?.name}`}
                </button>
              )}
            </>
          ) : (
            <div className="business-join-signin-prompt">
              <p>Sign in to {isInviteMode ? 'accept this invitation' : 'join this organization'}</p>
              <button
                className="business-join-btn business-join-btn-primary"
                onClick={() => {
                  const currentPath = window.location.pathname;
                  navigate(`/?redirect=${encodeURIComponent(currentPath)}`);
                }}
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        {!isInviteMode && (
          <>
            <div className="business-join-divider">
              <span>or</span>
            </div>

            <div className="business-join-body">
              <form className="business-join-code-form" onSubmit={handleCodeSubmit}>
                <label className="business-join-code-label" htmlFor="join-code">
                  Enter Join Code
                </label>
                <div className="business-join-code-input-row">
                  <input
                    id="join-code"
                    type="text"
                    className="business-join-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                    placeholder="ABC123"
                    maxLength={6}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="business-join-btn business-join-btn-primary"
                    disabled={codeSubmitting || code.trim().length === 0}
                  >
                    {codeSubmitting ? 'Joining...' : 'Join'}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
